import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { useMoney } from '../../../hooks/useMoney';

interface TransactionData {
  date: string;
  amount: number;
}

interface ChartPoint {
  label: string;
  amount: number;
  tooltipTitle: string;
}

interface TransactionChartProps {
  transactionData: TransactionData[];
  fromDate?: string;
  toDate?: string;
}

const MAX_BARS = 10;
const DAY_MS = 86400000;

const toDayStart = (input: string | Date): number => {
  if (input instanceof Date) {
    const d = new Date(input);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }

  const ymd = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }

  const dmy = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }

  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return NaN;
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const formatLabel = (ms: number, locale: string) =>
  new Date(ms).toLocaleDateString(locale, { day: 'numeric', month: 'short' });

const formatTooltipDate = (ms: number, locale: string) =>
  new Date(ms).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const groupTransactionDataForChart = (
  data: TransactionData[],
  fromDate: string | undefined,
  toDate: string | undefined,
  locale: string,
  maxBars: number = MAX_BARS
): ChartPoint[] => {
  const parsed = data
    .map((item) => ({ ...item, ts: toDayStart(item.date) }))
    .filter((item) => !Number.isNaN(item.ts))
    .sort((a, b) => a.ts - b.ts);

  if (parsed.length === 0) return [];

  const dataStart = parsed[0].ts;
  const dataEnd = parsed[parsed.length - 1].ts;

  let rangeStart = fromDate ? toDayStart(fromDate) : dataStart;
  let rangeEnd = toDate ? toDayStart(toDate) : dataEnd;
  if (Number.isNaN(rangeStart)) rangeStart = dataStart;
  if (Number.isNaN(rangeEnd)) rangeEnd = dataEnd;
  if (rangeStart > rangeEnd) [rangeStart, rangeEnd] = [rangeEnd, rangeStart];

  const rangeDays =
    Math.floor((rangeEnd - rangeStart) / DAY_MS) + 1;

  // Short range with few points: one bar per day (no combining)
  if (parsed.length <= maxBars && rangeDays <= maxBars) {
    return parsed.map((item) => ({
      label: formatLabel(item.ts, locale),
      amount: item.amount,
      tooltipTitle: formatTooltipDate(item.ts, locale),
    }));
  }

  const rangeEndExclusive = rangeEnd + DAY_MS;
  const bucketMs = (rangeEndExclusive - rangeStart) / maxBars;

  const buckets: ChartPoint[] = [];

  for (let i = 0; i < maxBars; i++) {
    const bucketStart = rangeStart + Math.floor(i * bucketMs);
    const bucketEndExclusive = rangeStart + Math.floor((i + 1) * bucketMs);

    const amount = parsed
      .filter((item) => item.ts >= bucketStart && item.ts < bucketEndExclusive)
      .reduce((sum, item) => sum + item.amount, 0);

    if (amount <= 0) continue;

    const bucketEnd = bucketEndExclusive - 1;
    buckets.push({
      label: formatLabel(bucketStart, locale),
      amount,
      tooltipTitle:
        bucketEnd - bucketStart < DAY_MS
          ? formatTooltipDate(bucketStart, locale)
          : `${formatTooltipDate(bucketStart, locale)} – ${formatTooltipDate(bucketEnd, locale)}`,
    });
  }

  return buckets;
};

const getYAxisUnit = (maxValue: number, symbol: string, locale: string) => {
  const isIndia = locale === 'en-IN';
  const step = (divisor: number, indiaLabel: string) => ({
    label: isIndia ? indiaLabel : `In  ${symbol}${divisor.toLocaleString(locale)}s`,
    divisor,
  });
  if (maxValue >= 100000) return step(100000, `In  ${symbol} Lakhs`);
  if (maxValue >= 50000) return step(50000, `In  ${symbol}50,000s`);
  if (maxValue >= 10000) return step(10000, `In  ${symbol}10,000s`);
  if (maxValue >= 1000) return step(1000, `In  ${symbol}1000s`);
  if (maxValue >= 100) return step(100, `In  ${symbol}100s`);
  if (maxValue >= 10) return step(10, `In  ${symbol}10s`);
  return { label: `Amount (${symbol})`, divisor: 1 };
};

const TransactionChart: React.FC<TransactionChartProps> = ({
  transactionData,
  fromDate,
  toDate,
}) => {
  const { symbol, locale } = useMoney();
  const chartData = useMemo(
    () => groupTransactionDataForChart(transactionData, fromDate, toDate, locale),
    [transactionData, fromDate, toDate, locale]
  );

  if (!transactionData?.length || chartData.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No transaction data available
      </div>
    );
  }

  const maxAmount = Math.max(...chartData.map((d) => d.amount), 0);
  const { label, divisor } = getYAxisUnit(maxAmount, symbol, locale);

  const barChartData = {
    labels: chartData.map((d) => d.label),
    datasets: [
      {
        label: 'Amount',
        data: chartData.map((d) => d.amount),
        backgroundColor: '#4A90E2',
        borderRadius: 4,
        maxBarThickness: 56,
      },
    ],
  };

  const barChartOptions = {
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: label,
          padding: { bottom: 12 },
        },
        ticks: {
          maxTicksLimit: 5,
          callback: function (tickValue: string | number) {
            const value =
              typeof tickValue === 'string' ? parseFloat(tickValue) : tickValue;
            return value / divisor;
          },
        },
      },
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: chartData.length > 6,
          maxTicksLimit: 10,
          font: { size: 11 },
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: { dataIndex: number }[]) => {
            const idx = items[0]?.dataIndex ?? 0;
            return chartData[idx]?.tooltipTitle ?? '';
          },
          label: (ctx: { raw: unknown }) => {
            const value = Number(ctx.raw);
            return `${symbol}${value.toLocaleString(locale)}`;
          },
        },
      },
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  return (
    <div className="w-full h-56 px-2">
      <Bar data={barChartData} options={barChartOptions} />
    </div>
  );
};

export default TransactionChart;
